import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '@/components/snackbar';
import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolvePaymentMethodType } from '@/consts/payment-method-types';
import { getPaymentMethodIconifyIcon, PAYMENT_METHOD_DISPLAY_NAMES } from '@/consts/payment-methods';
import useOrderBumpGrouping from '../hooks/useOrderBumpGrouping';
import useTableFilter from '../hooks/useTableFilter';
import { BACKGROUND_QUERY_META } from '../utils/backgroundQuery';
import {
  clearOrdersCache,
  exportReport,
  getGroupedOrders,
  getOrders,
  refundOrderService,
  resendOrderEmailService,
} from '../services/orders';
import {
  DEFAULT_PRODUCT_CURRENCY_CODE,
  DOLARIZATION_SUPPORTED_CURRENCIES,
} from '../utils/currency';

export const OrderType = {
  unique: {
    label: 'Pagamento único',
    color: 'primary',
  },
  subscription: {
    label: 'Pagamento recorrente',
    color: 'info',
  },
};

export const OrderStatus = {
  processing: {
    label: 'Processando',
    color: 'warning',
  },
  authorized: {
    label: 'Autorizado',
    color: 'success',
  },
  paid: {
    label: 'Pago',
    color: 'success',
  },
  refunded: {
    label: 'Reembolsado',
    color: 'error',
  },
  waiting_payment: {
    label: 'Aguardando pagamento',
    color: 'warning',
  },
  refused: {
    label: 'Recusado',
    color: 'error',
  },
  blocked: {
    label: 'Recusado',
    color: 'error',
  },
  chargedback: {
    label: 'Chargeback',
    color: 'error',
  },
  canceled: {
    label: 'Cancelado',
    color: 'error',
  },
  in_protest: {
    label: 'Em protesto',
    color: 'error',
  },
  partially_paid: {
    label: 'Parcialmente pago',
    color: 'warning',
  },
  MED: {
    label: 'MED',
    color: 'error',
  },
  refund_requested: {
    label: 'Reembolso solicitado',
    color: 'warning',
  },
  // Os mesmos rótulos do portal e do popover de defesa. "Em processamento" sozinho se confundia
  // com o pagamento pendente no filtro de Status, e "Falha" dizia que o estorno não saiu quando
  // `acquirer_error` também é o prazo de confirmação estourado, com o estorno ainda podendo liquidar.
  in_settlement: {
    label: 'Reembolso em processamento',
    color: 'warning',
  },
  acquirer_error: {
    label: 'Reembolso não concluído',
    color: 'error',
  },
  expired: {
    label: 'Expirado',
    color: 'warning',
  },
};

// O webhook do split que leva o pedido do core para `refunded` chega em segundos no estorno síncrono.
export const REFUND_REFETCH_DELAY_MS = 10000;

export const PERIODS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  CUSTOM: 'custom',
  ALL_TIME: 'all_time',
};

export const OrderCoproductionType = {
  all: {
    label: 'Todos',
    value: 'all',
  },
  production: {
    label: 'Sou produtor',
    value: 'production',
  },
  coproduction: {
    label: 'Sou co-produtor',
    value: 'coproduction',
  },
  affiliate: {
    label: 'Sou afiliado',
    value: 'affiliate',
  },
};

export const OrderSubscriptionType = {
  renewed: {
    label: 'Renovação',
    value: 'renewed',
  },
  new: {
    label: 'Novo',
    value: 'new',
  },
};

function createOrderPaymentMethod(value, icon, fallbackLabel) {
  return {
    value,
    label: PAYMENT_METHOD_DISPLAY_NAMES[value] || fallbackLabel || value || '',
    icon,
  };
}

export const OrderPaymentMethod = {
  pix: createOrderPaymentMethod('pix', getPaymentMethodIconifyIcon('pix'), 'Pix'),
  pix_auto: createOrderPaymentMethod('pix_auto', getPaymentMethodIconifyIcon('pix_auto'), 'PIX Automático'),
  credit_card: createOrderPaymentMethod(
    'credit_card',
    getPaymentMethodIconifyIcon('credit_card'),
    'Cartão de crédito'
  ),
  picpay: createOrderPaymentMethod('picpay', getPaymentMethodIconifyIcon('picpay'), 'Pic Pay'),
  applepay: createOrderPaymentMethod('applepay', getPaymentMethodIconifyIcon('applepay'), 'Apple Pay'),
  googlepay: createOrderPaymentMethod(
    'googlepay',
    getPaymentMethodIconifyIcon('googlepay'),
    'Google Pay'
  ),
  openfinance_nubank: createOrderPaymentMethod(
    'openfinance_nubank',
    getPaymentMethodIconifyIcon('openfinance_nubank'),
    'Nubank'
  ),
  paypal_wallet: createOrderPaymentMethod(
    'paypal_wallet',
    getPaymentMethodIconifyIcon('paypal_wallet'),
    'PayPal'
  ),
  pagaleve: createOrderPaymentMethod(
    'pagaleve',
    getPaymentMethodIconifyIcon('pagaleve'),
    'Parcelamento no Pix'
  ),
  // Pagamento dividido. Entrar aqui só ensina o painel a desenhar o método: quem decide quem o vê
  // é o backend, que só o lista no catálogo de métodos do produtor com a feature experimental.
  // Nenhum pedido é gravado com eles (cada parte fica com o meio real), por isso o filtro de vendas
  // os deixa de fora.
  multi_card: createOrderPaymentMethod(
    'multi_card',
    getPaymentMethodIconifyIcon('multi_card'),
    'Dois cartões'
  ),
  card_pix: createOrderPaymentMethod('card_pix', getPaymentMethodIconifyIcon('card_pix'), 'Cartão + Pix'),
  mercadopago_wallet: createOrderPaymentMethod(
    'mercadopago_wallet',
    getPaymentMethodIconifyIcon('mercadopago_wallet'),
    'Mercado Pago'
  ),
  boleto: createOrderPaymentMethod('boleto', getPaymentMethodIconifyIcon('boleto'), 'Boleto'),
  threeDs: createOrderPaymentMethod('threeDs', getPaymentMethodIconifyIcon('threeDs'), '3DS'),
  oxxo: createOrderPaymentMethod('oxxo', getPaymentMethodIconifyIcon('oxxo'), 'OXXO'),
  spei: createOrderPaymentMethod('spei', getPaymentMethodIconifyIcon('spei'), 'SPEI'),
  debit_card: createOrderPaymentMethod(
    'debit_card',
    getPaymentMethodIconifyIcon('debit_card'),
    'Cartão de débito'
  ),
  pse: createOrderPaymentMethod('pse', getPaymentMethodIconifyIcon('pse'), 'PSE'),
  nequi: createOrderPaymentMethod('nequi', getPaymentMethodIconifyIcon('nequi'), 'Nequi'),
  safetypay: createOrderPaymentMethod(
    'safetypay',
    getPaymentMethodIconifyIcon('safetypay'),
    'SafetyPay'
  ),
};

export function getOrderPaymentMethodLabel(paymentMethod, fallback = '-') {
  if (!paymentMethod) return fallback;

  const normalizedKey = resolvePaymentMethodType(paymentMethod);

  return OrderPaymentMethod[normalizedKey]?.label || fallback;
}

export const OrdersContext = createContext({
  table: {},
  orders: [],
  fetching: false,
  isError: false,
  error: null,
  refetch: () => {},
  total: 0,
  refund: [],
  status: 'paid',
  setStatus: () => {},
  currency: DEFAULT_PRODUCT_CURRENCY_CODE,
  setCurrency: () => {},
  currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
  ordersCount: 0,
  ordersAmount: 0,
  ordersRefundAmount: 0,
  ordersPixAmount: 0,
  ordersCardAmount: 0,
  refundPercentage: 0,
  ordersChargebackAmount: 0,
  mutateExport: () => {},
  loadingExport: false,
  resendOrderEmail: () => {},
  refundRequestsCount: 0,
});

export const defaultFilter = {
  period: PERIODS.ALL_TIME,
  startDate: null,
  endDate: null,
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
  sck: null,
  utm_exact_match: false,
  coproductionType: OrderCoproductionType.all.value,
  products: [],
  offerType: 'all',
  offers: [],
  affiliates: [],
  countries: [],
  paymentMethods: [],
  status: [],
  subscription_type: [],
};

const OrdersProvider = ({ children }) => {
  const notify = useNotify();
  const table = useTableFilter({
    defaultCurrentPage: 1,
    defaultFilter,
  });

  // Nasce sem status, e sem status nada de /orders/ é pedido: quem diz qual lista
  // está na tela é a página. Começar em `paid` fazia a aba aberta direto pelo link
  // pedir a lista de aprovadas no mount, antes de a página trocar o status, e as
  // abas que não são de vendas (Relatórios, Cupons) nunca devem pedir: o /orders/
  // soma os totais do período antes de filtrar o status.
  const [status, setStatus] = useState(null);
  const hasSalesList = status !== null;
  const [currency, setCurrency] = useState(DEFAULT_PRODUCT_CURRENCY_CODE);
  const queryClient = useQueryClient();

  // Sem a função experimental `grouping.grouped` é sempre falso e a tela lê a /orders/ de sempre.
  const grouping = useOrderBumpGrouping();
  const groupBumps = grouping.grouped;
  const { setGrouped } = grouping;
  const { setPage } = table;
  // A página volta para a primeira: o `count` das duas listas é diferente (compras x pedidos).
  const setGroupBumps = useCallback(
    (value) => {
      setGrouped(value);
      setPage(1);
    },
    [setGrouped, setPage]
  );

  // A página é da lista, não da tela: quem troca de status pede a primeira página da lista
  // nova, senão a página da anterior vai no request e o /orders/ responde 404 além da última.
  // Comparação por valor porque a aba Reembolsadas manda um array de status; a passagem de
  // `null` para o primeiro status é o mount, não uma troca de aba.
  const statusRef = useRef(status);
  const changeStatus = useCallback(
    (nextStatus) => {
      const previous = statusRef.current;
      statusRef.current = nextStatus;
      if (previous !== null && String(previous) !== String(nextStatus)) setPage(1);
      setStatus(nextStatus);
    },
    [setPage]
  );

  const {
    data: {
      results: orders,
      count: total,
      ordersCount,
      ordersAmount,
      ordersRefundAmount,
      ordersPixAmount,
      ordersCardAmount,
      refundPercentage,
      ordersChargebackAmount,
    },
    isFetching: fetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryFn: async () =>
      (groupBumps ? getGroupedOrders : getOrders)({
        page: table.page,
        search: table.search,
        status,
        filter: table.filter,
        currency,
      }),
    queryKey: ['orders', table.page, table.search, status, table.filter, currency, groupBumps],
    enabled: hasSalesList,
    initialData: {
      results: [],
      total: 0,
      ordersCount: 0,
      ordersAmount: 0,
      ordersRefundAmount: 0,
      ordersPixAmount: 0,
      ordersCardAmount: 0,
      refundPercentage: 0,
      ordersChargebackAmount: 0,
    },
  });

  // Página além da última (404) com página > 1: a lista encolheu por trás (reembolso, "Atualizar")
  // e o "Tentar Novamente" repetiria a mesma página. Voltar à primeira refaz a consulta; na
  // primeira o erro fica, sem laço.
  const { page } = table;
  useEffect(() => {
    if (isError && error?.response?.status === 404 && page > 1) setPage(1);
  }, [isError, error, page, setPage]);

  // A venda sai da lista (ou muda de status nela) só depois da resposta: é o back que decide se o
  // reembolso liquidou na hora ou ficou em processamento. Tirar antes, no `onMutate`, a fazia sumir
  // de Aprovadas e voltar ao recarregar quando a adquirente ainda não tinha confirmado.
  const settleRefundedOrder = useCallback(
    (orderId, nextStatus) => {
      queryClient.getQueriesData({ queryKey: ['orders'] }).forEach(([key, list]) => {
        if (!list?.results) return;
        // `status` é a 4ª posição da chave: vazio na aba Todas, lista na aba Reembolsadas.
        const listStatus = key[3];
        const stillListed =
          !listStatus || listStatus === 'all' || [].concat(listStatus).includes(nextStatus);
        queryClient.setQueryData(key, {
          ...list,
          results: stillListed
            ? list.results.map((order) =>
                order.id === orderId ? { ...order, status: nextStatus } : order
              )
            : list.results.filter((order) => order.id !== orderId),
        });
      });
    },
    [queryClient]
  );

  const { mutateAsync: refund } = useMutation({
    mutationKey: ['orders.refund'],
    mutationFn: (data) => refundOrderService({ data }),
    onSuccess: async (result, order) => {
      // Sem `status` o estorno foi síncrono: a adquirente já devolveu, e o pedido do core só segue
      // `paid` até o webhook do split chegar.
      const nextStatus = result?.status || 'refunded';
      await queryClient.cancelQueries({ queryKey: ['orders'] });
      clearOrdersCache();
      settleRefundedOrder(order.id, nextStatus);
      notify.success(result?.detail || 'Reembolso solicitado com sucesso!');
      // Buscar na hora pegava o pedido ainda `paid`, antes do webhook, e o gravava no `ordersCache`
      // por 10 min, com o Atualizar bloqueado por 1 min: a venda ficava em Aprovadas depois do toast.
      setTimeout(() => {
        clearOrdersCache();
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      }, REFUND_REFETCH_DELAY_MS);
    },
    // O `detail` do back primeiro: o segundo clique num reembolso já em processamento volta
    // como 400, e "não foi possível reembolsar" leria como falha um estorno em andamento.
    onError: (refundError) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      notify.error(refundError?.response?.data?.detail || 'Não foi possível reembolsar a venda!');
    },
  });

  const { mutate: mutateExport, isLoading: loadingExport } = useMutation({
    async mutationFn(params) {
      return exportReport({
        ...params,
        page: table.page,
        search: table.search,
        status: status === 'all' ? '' : status,
        filter: table.filter,
        currency,
      });
    },
    onSuccess(result) {
      clearOrdersCache();
      queryClient.invalidateQueries({ queryKey: ['orders'] });

      if (result?.empty) {
        notify.warning('Não há vendas para exportar com os filtros aplicados.');
      } else if (result?.emailed) {
        notify.info('O relatório será enviado por e-mail.');
      } else {
        notify.success('Exportação realizada com sucesso.');
      }
    },
    onError() {
      notify.error('Ocorreu um erro com a exportação!');
    },
  });

  const {
    data: { count: refundRequestsCount },
  } = useQuery({
    queryKey: ['orders', 'refundRequestsCount', currency],
    queryFn: () =>
      getOrders({
        page: 1,
        status: 'refund_requested',
        filter: defaultFilter,
        currency,
      }),
    initialData: { count: 0 },
    refetchInterval: 60000,
    meta: BACKGROUND_QUERY_META,
    // Só o banner da aba Reembolsadas lê esta contagem; o selo da barra lateral tem
    // a consulta dele.
    enabled: hasSalesList,
  });

  const { mutate: resendOrderEmail } = useMutation({
    mutationFn: (orderId) => resendOrderEmailService(orderId),
    onSuccess({ detail }) {
      notify.success(detail);
    },
    onError(resendError) {
      const message =
        resendError?.response?.data?.detail ??
        'Não foi possível reenviar o e-mail. Tente novamente mais tarde.';
      notify.error(message);
    },
  });

  const value = useMemo(
    () => ({
      table,
      orders,
      fetching,
      isError,
      error,
      refetch,
      total,
      refund,
      status,
      setStatus: changeStatus,
      currency,
      setCurrency,
      currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
      ordersCount,
      ordersAmount,
      ordersRefundAmount,
      ordersPixAmount,
      ordersCardAmount,
      refundPercentage,
      ordersChargebackAmount,
      mutateExport,
      loadingExport,
      resendOrderEmail,
      refundRequestsCount,
      groupingAvailable: grouping.available,
      groupBumps,
      setGroupBumps,
    }),
    [
      table,
      orders,
      fetching,
      isError,
      error,
      refetch,
      refund,
      total,
      status,
      changeStatus,
      currency,
      ordersCount,
      ordersAmount,
      ordersRefundAmount,
      ordersPixAmount,
      ordersCardAmount,
      refundPercentage,
      ordersChargebackAmount,
      mutateExport,
      loadingExport,
      resendOrderEmail,
      refundRequestsCount,
      grouping.available,
      groupBumps,
      setGroupBumps,
    ]
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
};

OrdersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default OrdersProvider;
